import { store } from '@/store/store';
import { MapItem } from '@/store/mapsSlice';

/**
 * Convert a version string (YYYYMMDD) to a quarter string (YYYYQN)
 * @param version The version string in YYYYMMDD format
 * @returns A string in the format YYYYQN (e.g., "2023Q1")
 */
export const versionToQuarter = (version: string): string => {
  if (!version || version.length < 6) return '';

  try {
    const year = version.slice(0, 4);
    const month = parseInt(version.slice(4, 6));

    let quarter = 1;
    if (month >= 4 && month <= 6) quarter = 2;
    else if (month >= 7 && month <= 9) quarter = 3;
    else if (month >= 10) quarter = 4;

    return `${year}Q${quarter}`;
  } catch (error) {
    console.error(`Error converting version to quarter: ${version}`, error);
    return '';
  }
};

/**
 * Get plans for a specific company and version
 * @param slug The company slug
 * @param version The version to get plans for (optional - uses most recent if not specified)
 * @returns An object containing the version and an array of plans applicable to that version
 */
export const getPlans = (fullSlug: string, version?: string): { version: string | null, data: any[] } => {
  console.log(`🔍 [getPlans] Fetching plans for company: ${fullSlug}, version: ${version}`);
  
  // Get state from Redux store
  const state = store.getState();
  const company = state.company.company;
  const maps = state.maps.maps;

  // If no company or maps data, return empty result
  if (!company || !maps || maps.length === 0) {
    return { version: null, data: [] };
  }

  // Parse fullSlug to extract slug and subslug (if present)
  const slugParts = fullSlug.split('.');
  const slug = slugParts[0];
  const hasSubslug = slugParts.length > 1;
  const rawSubslug = hasSubslug ? slugParts[1] : '';
  const subslug = rawSubslug.replace(/^\./, '');
  console.log(`🔍 [DEBUG-PLANS] SubSlug detection: hasSubslug=${hasSubslug}, slug="${slug}", rawSubslug="${rawSubslug}", cleanedSubslug="${subslug}"`);

  // Get all pages for the company, filtered by subslug if provided
  const allPages = company.pages || [];
  if (allPages.length === 0) {
    return { version: null, data: [] };
  }

  // Filter pages by subslug
  const pages = hasSubslug
    ? allPages.filter(page => {
        const cleanPageSubslug = (page.subslug || '').replace(/^\./, '');
        return cleanPageSubslug === subslug;
      })
    : allPages.filter(page => !page.subslug || page.subslug === '');
  console.log(`🔍 [DEBUG-PLANS] Filtered to ${pages.length} pages based on${hasSubslug ? ` subslug: "${subslug}"` : ' main page'}`);

  // If no pages match the subslug, return empty result
  if (pages.length === 0) {
    console.log(`⚠️ [WARNING-PLANS] No pages found matching subslug: "${subslug}"`);
    return { version: null, data: [] };
  }

  // Get all plans from filtered pages
  const allPlans = pages.flatMap(page => 
    (page.plans && Array.isArray(page.plans)) 
      ? page.plans.map(plan => ({ ...plan, page_id: page.page_id }))
      : []
  );

  // If no plans found, return empty result
  if (allPlans.length === 0) {
    return { version: null, data: [] };
  }

  // Find the version to use
  let versionToUse = version;
  if (!versionToUse) {
    // Extract all versions and sort in descending order
    const allVersions = maps
      .map(map => map.version)
      .filter(Boolean)
      .sort((a, b) => b.localeCompare(a));
    
    if (allVersions.length === 0) {
      return { version: null, data: [] };
    }
    
    // Use the most recent version
    versionToUse = allVersions[0];
  }

  // Get the version data for the version we're using
  const versionMap = maps.find(map => map.version === versionToUse);
  if (!versionMap) {
    return { version: versionToUse, data: [] };
  }
  
  // Get the map data (may be in map_data property or directly on the map object)
  const mapData = versionMap.map_data || versionMap;

  // Extract plan, charge, and discount IDs from the version
  // In the format provided by the user, these are arrays of strings
  const planIds = new Set<string>();
  const chargeIds = new Set<string>();
  const discountIds = new Set<string>();

  // Handle plans array (could be an array of strings or objects)
  if (Array.isArray(mapData.plans)) {
    mapData.plans.forEach((planItem: any) => {
      if (typeof planItem === 'string') {
        planIds.add(planItem);
      } else if (planItem && planItem.plan_id) {
        planIds.add(planItem.plan_id);
      }
    });
  }

  // Handle charges array (could be an array of strings or objects)
  if (Array.isArray(mapData.charges)) {
    mapData.charges.forEach((chargeItem: any) => {
      if (typeof chargeItem === 'string') {
        chargeIds.add(chargeItem);
      } else if (chargeItem && chargeItem.charge_id) {
        chargeIds.add(chargeItem.charge_id);
      }
    });
  }
  
  // Handle discounts array (could be an array of strings or objects)
  if (Array.isArray(mapData.discounts)) {
    mapData.discounts.forEach((discountItem: any) => {
      if (typeof discountItem === 'string') {
        discountIds.add(discountItem);
      } else if (discountItem && discountItem.discount_id) {
        discountIds.add(discountItem.discount_id);
      }
    });
  }

  // Log what we found
  console.log(`🔍 [getPlans] Plan IDs in version ${versionToUse}:`, Array.from(planIds));
  console.log(`🔍 [getPlans] Charge IDs in version ${versionToUse}:`, Array.from(chargeIds));
  console.log(`🔍 [getPlans] Discount IDs in version ${versionToUse}:`, Array.from(discountIds));

  // If no plan IDs found, return empty result
  if (planIds.size === 0) {
    console.log(`⚠️ [getPlans] No plan IDs found for version ${versionToUse}`);
    return { version: null, data: [] };
  }
  
  // Note: We now allow versions with no charge IDs (plans with no charges are valid)
  if (chargeIds.size === 0) {
    console.log(`ℹ️ [getPlans] No charge IDs found for version ${versionToUse}, but we have plans - proceeding`);
  }

  // Filter plans to only include those that exist in the version and deduplicate by ID
  // First, create a map to deduplicate plans by ID
  const planMap = new Map();
  
  allPlans
    .filter(plan => plan.id && planIds.has(plan.id))
    .forEach(plan => {
      // If we already have this plan ID in our map, skip it
      if (!planMap.has(plan.id)) {
        // Create a deep copy of the plan
        const planCopy = { ...plan };

        // Filter and deduplicate charges
        if (planCopy.charges && Array.isArray(planCopy.charges)) {
          // Use a map to deduplicate charges by ID
          const chargeMap = new Map();
          
          // Only keep charges that exist in the version
          planCopy.charges
            .filter(charge => charge.id && chargeIds.has(charge.id))
            .forEach(charge => {
              // Store only one instance of each charge ID
              if (!chargeMap.has(charge.id)) {
                chargeMap.set(charge.id, charge);
              }
            });
          
          // Convert back to array
          planCopy.charges = Array.from(chargeMap.values());
        } else {
          planCopy.charges = [];
        }
        
        // Filter and deduplicate discounts
        if (planCopy.discounts && Array.isArray(planCopy.discounts)) {
          // Use a map to deduplicate discounts by ID
          const discountMap = new Map();
          
          // Only keep discounts that exist in the version
          planCopy.discounts
            .filter(discount => discount.id && discountIds.has(discount.id))
            .forEach(discount => {
              // Store only one instance of each discount ID
              if (!discountMap.has(discount.id)) {
                discountMap.set(discount.id, discount);
              }
            });
          
          // Convert back to array
          planCopy.discounts = Array.from(discountMap.values());
        } else {
          planCopy.discounts = [];
        }

        // Add to our map
        planMap.set(plan.id, planCopy);
      }
    });

  // Convert map values to array
  const filteredPlans = Array.from(planMap.values());

  // Log stats about what we found
  console.log(`📊 [getPlans] For version ${versionToUse}:`);
  console.log(`  - Found ${planIds.size} plan IDs in version`);
  console.log(`  - Found ${chargeIds.size} charge IDs in version`);
  console.log(`  - Found ${discountIds.size} discount IDs in version`);
  console.log(`  - Filtered to ${filteredPlans.length} unique plans after deduplication`);
  console.log("bbbb", filteredPlans);
  
  // Count plans with no charges
  const plansWithNoCharges = filteredPlans.filter(plan => 
    !plan.charges || plan.charges.length === 0
  ).length;
  console.log(`  - ${plansWithNoCharges} plans have no charges`);

  // Return the version and filtered plans
  return {
    version: versionToUse,
    data: filteredPlans
  };
}

/**
 * Get pricing metrics for a specific company
 * @param fullSlug The company slug, optionally with subslug (company/subslug)
 * @returns An object containing all metrics found on the page
 */
export const getMetrics = (fullSlug: string): { version: string | null, data: any[] } => {
  console.log(`🔄 [DEBUG] getMetrics called with fullSlug: "${fullSlug}"`);
  
  // Get state from Redux store
  const state = store.getState();

  // Get company data
  const company = state.company.company;
  
  // If no company data, return empty result
  if (!company) {
    console.log(`ℹ️ [getMetrics] No company data found for fullSlug: ${fullSlug}`);
    return { version: null, data: [] };
  }

  // Parse fullSlug to extract slug and subslug (if present)
  const slugParts = fullSlug.split('.');
  const slug = slugParts[0];
  const hasSubslug = slugParts.length > 1;
  const rawSubslug = hasSubslug ? slugParts[1] : '';
  const subslug = rawSubslug.replace(/^\./, '');
  console.log(`🔍 [DEBUG-METRICS] SubSlug detection: hasSubslug=${hasSubslug}, slug="${slug}", rawSubslug="${rawSubslug}", cleanedSubslug="${subslug}"`);

  // Get all pages for the company, filtered by subslug if provided
  const allPages = company.pages || [];
  if (allPages.length === 0) {
    console.log(`ℹ️ [getMetrics] No pages found for company: ${slug}`);
    return { version: null, data: [] };
  }

  // Filter pages by subslug
  const pages = hasSubslug
    ? allPages.filter(page => {
        const cleanPageSubslug = (page.subslug || '').replace(/^\./, '');
        return cleanPageSubslug === subslug;
      })
    : allPages.filter(page => !page.subslug || page.subslug === '');
  console.log(`🔍 [DEBUG-METRICS] Filtered to ${pages.length} pages based on${hasSubslug ? ` subslug: "${subslug}"` : ' main page'}`);
  
  // If no pages match the subslug, return empty result (don't fall back to all pages)
  if (pages.length === 0) {
    console.log(`⚠️ [WARNING-METRICS] No pages found matching subslug: "${subslug}"`);
    return { version: null, data: [] };
  }
  
  // If filtered pages is still empty, return empty result
  if (pages.length === 0) {
    console.log(`ℹ️ [getMetrics] No matching pages found for: ${fullSlug}`);
    return { version: null, data: [] };
  }

  // Get all pricing metrics from all pages
  let allMetrics: any[] = [];

  pages.forEach(page => {
    // Check if page has pricing_metrics
    if (page.pricing_metrics && Array.isArray(page.pricing_metrics)) {
      console.log(`✅ [getMetrics] Found ${page.pricing_metrics.length} metrics on page ${page.page_id || page.id}`);

      // Add page_id to each metric for reference
      const metricsWithPageId = page.pricing_metrics.map(metric => ({
        ...metric,
        page_id: page.id || page.page_id
      }));

      allMetrics = [...allMetrics, ...metricsWithPageId];
    } else {
      console.log(`ℹ️ [getMetrics] No pricing_metrics found on page ${page.page_id || page.id}`);
    }
  });

  // If no metrics found, return empty result
  if (allMetrics.length === 0) {
    console.log(`ℹ️ [getMetrics] No metrics found for company: ${slug}`);
    return { version: null, data: [] };
  }

  console.log(`✅ [getMetrics] Found a total of ${allMetrics.length} metrics for ${slug}`);

  // Get all plans only from the filtered pages to find associated plans for each metric
  const plans = pages
    .flatMap(page => page.plans || [])
    .filter(plan => plan && plan.id);

  console.log(`🔍 [getMetrics] Found ${plans.length} plans to process for metrics from ${pages.length} filtered pages`);

  // Create a map of pricing_metric_id to associated plans
  // We'll use a Map where the values are Maps with plan IDs as keys to ensure uniqueness
  const metricToPlanMap = new Map<string, Map<string, { id: string, name: string, displayName: string }>>();

  // Process all plans and their charges to build the metric-to-plan mapping
  plans.forEach(plan => {
    if (plan.charges && Array.isArray(plan.charges)) {
      console.log(`🔍 [getMetrics] Plan ${plan.plan_name || plan.display_name || plan.id} has ${plan.charges.length} charges`);

      plan.charges.forEach(charge => {
        // Check for pricing_metric_id in different possible properties
        const metricId = charge.pricing_metric_id || charge.pricing_metric || charge.metric_id;

        if (metricId) {
          console.log(`✅ [getMetrics] Found charge with metric ID: ${metricId}`);

          if (!metricToPlanMap.has(metricId)) {
            metricToPlanMap.set(metricId, new Map());
          }

          // Use the plan ID as the key to ensure uniqueness
          metricToPlanMap.get(metricId)?.set(plan.id, {
            id: plan.id,
            name: plan.plan_name || '',
            displayName: plan.display_name || '',
            price: charge.charge_amount,
            unit: charge.price_per,
            source: 'charge_pricing_metric_id'
          });
        } else {
          // If no direct ID, try to match by price_per to metric display_name
          const pricePer = charge.price_per;
          if (pricePer) {
            // Look for a metric with matching display_name
            const matchingMetric = allMetrics.find(m =>
              m.display_name && pricePer.toLowerCase().includes(m.display_name.toLowerCase())
            );

            if (matchingMetric) {
              console.log(`✅ [getMetrics] Matched charge by price_per (${pricePer}) to metric: ${matchingMetric.display_name}`);

              if (!metricToPlanMap.has(matchingMetric.id)) {
                metricToPlanMap.set(matchingMetric.id, new Map());
              }

              // Use the plan ID as the key to ensure uniqueness
              metricToPlanMap.get(matchingMetric.id)?.set(plan.id, {
                id: plan.id,
                name: plan.plan_name || '',
                displayName: plan.display_name || '',
                price: charge.charge_amount,
                unit: charge.price_per,
                source: 'price_per_match'
              });
            } else {
              console.log(`ℹ️ [getMetrics] Could not match charge by price_per: ${pricePer}`);
            }
          } else {
            console.log(`ℹ️ [getMetrics] Charge without pricing_metric_id or price_per: ${JSON.stringify(charge)}`);
          }
        }
      });
    } else {
      console.log(`ℹ️ [getMetrics] Plan ${plan.plan_name || plan.display_name || plan.id} has no charges`);
    }
  });

  // Log the number of metrics with associated plans
  console.log(`✅ [getMetrics] Found ${metricToPlanMap.size} metrics with associated plans`);

  // Add associated plans to each metric
  console.log(`🔍 [getMetrics] Processing ${allMetrics.length} metrics to add associated plans`);
  console.log(`🔍 [getMetrics] Metric-to-plan map has ${metricToPlanMap.size} entries`);

  // Based on the sample data, metrics have their own structure and are not directly linked to plans
  // We need to associate metrics with plans using charge data
  console.log(`🔍 [getMetrics] Processing metrics based on sample structure`);
  
  // First, check if we have company-level metrics
  if (company.metrics && Array.isArray(company.metrics)) {
    console.log(`✅ [getMetrics] Found ${company.metrics.length} metrics at company level`);
    
    // Add these metrics to allMetrics if they're not already there
    company.metrics.forEach((metric: any) => {
      if (!metric || !metric.id) {
        console.log(`⚠️ [getMetrics] Found invalid metric in company.metrics`);
        return;
      }
      
      // Check if this metric is already in allMetrics
      const metricExists = allMetrics.some(m => m.id === metric.id);
      if (!metricExists) {
        allMetrics.push(metric);
        console.log(`✅ [getMetrics] Added company-level metric: ${metric.display_name || metric.id}`);
      }
    });
  }
  
  // Then check for page-level metrics from FILTERED pages only
  pages.forEach(page => {
    if (page.metrics && Array.isArray(page.metrics)) {
      console.log(`✅ [getMetrics] Found ${page.metrics.length} metrics in FILTERED page ${page.id || page.page_id}`);
      
      // Add these metrics to allMetrics if they're not already there
      page.metrics.forEach((metric: any) => {
        if (!metric || !metric.id) {
          console.log(`⚠️ [getMetrics] Found invalid metric in page ${page.id || page.page_id}`);
          return;
        }
        
        // Check if this metric is already in allMetrics
        const metricExists = allMetrics.some(m => m.id === metric.id);
        if (!metricExists) {
          // Add page_id to the metric
          const enrichedMetric = {
            ...metric,
            page_id: page.id || page.page_id
          };
          
          allMetrics.push(enrichedMetric);
          console.log(`✅ [getMetrics] Added page-level metric: ${metric.display_name || metric.id} from page ${page.id}`);
        }
      });
    }
    
    // Also check for plan-level metrics (for filtered pages only)
    if (page.plans && Array.isArray(page.plans)) {
      page.plans.forEach(plan => {
        if (plan.metrics && Array.isArray(plan.metrics)) {
          console.log(`✅ [getMetrics] Found ${plan.metrics.length} metrics in plan ${plan.id} from FILTERED page ${page.id}`);
          
          // For each metric, add it to allMetrics and create a plan association
          plan.metrics.forEach((metric: any) => {
            if (!metric || !metric.id) {
              console.log(`⚠️ [getMetrics] Found invalid metric in plan ${plan.id}`);
              return;
            }
            
            const metricId = metric.id;
            
            // Add to our metricToPlanMap
            if (!metricToPlanMap.has(metricId)) {
              metricToPlanMap.set(metricId, new Map());
            }
            
            // Use the plan ID as the key to ensure uniqueness
            metricToPlanMap.get(metricId)?.set(plan.id, {
              id: plan.id,
              name: plan.plan_name || '',
              displayName: plan.display_name || '',
              // We might have sample prices in the metric
              price: metric.sample_prices && metric.sample_prices.length > 0 ? metric.sample_prices[0] : undefined,
              unit: metric.unit,
              source: 'plan_metrics'
            });
            
            // Check if this metric is already in allMetrics
            const metricExists = allMetrics.some(m => m.id === metricId);
            if (!metricExists) {
              // Add page_id to the metric
              const enrichedMetric = {
                ...metric,
                page_id: page.id || page.page_id
              };
              
              allMetrics.push(enrichedMetric);
              console.log(`✅ [getMetrics] Added plan-level metric: ${metric.display_name || metric.id} from page ${page.id}`);
            }
          });
        }
      });
    }
  });
  
  console.log(`🔍 [getMetrics] Total metrics found: ${allMetrics.length}`);
  console.log(`🔍 [getMetrics] After processing all metrics, metricToPlanMap has ${metricToPlanMap.size} entries`);
  
  // Log the metrics that now have plans associated  
  for (const [metricId, planMap] of metricToPlanMap.entries()) {
    console.log(`🔍 [getMetrics] Metric ${metricId} has ${planMap.size} associated plans`);
  }

  const metricsWithPlans = allMetrics.map(metric => {
    // Try to find associated plans using different possible ID formats
    const metricId = metric.id;
    const metricPricingId = metric.pricing_metric_id;

    let planMap = metricToPlanMap.get(metricId);

    // If no plans found with the primary ID, try the pricing_metric_id
    if ((!planMap || planMap.size === 0) && metricPricingId && metricPricingId !== metricId) {
      planMap = metricToPlanMap.get(metricPricingId);
      if (planMap && planMap.size > 0) {
        console.log(`✅ [getMetrics] Found plans using pricing_metric_id: ${metricPricingId}`);
      }
    }

    // Convert the Map values to an array to get unique plans
    const associatedPlans = planMap ? Array.from(planMap.values()) : [];

    console.log(`🔍 [getMetrics] Metric ${metric.display_name || metric.id} has ${associatedPlans.length} associated plans`);
    if (associatedPlans.length > 0) {
      console.log(`✅ [getMetrics] Plans for metric ${metric.display_name || metric.id}: ${associatedPlans.map(p => p.displayName || p.name).join(', ')}`);
    }

    return {
      ...metric,
      associatedPlans
    };
  });

  // Return all metrics with associated plans
  // We'll keep the version as null since we're not filtering by version anymore
  return {
    version: null,
    data: metricsWithPlans
  };
};

/**
 * Get tags for a specific company and version
 * @param slug The company slug
 * @param version The version to get tags for (optional - uses most recent if not specified)
 * @param allVersions If true, returns all tags regardless of version
 * @returns An object containing the version and an array of tags applicable to that version
 */
export const getTags = (slug: string, version?: string, allVersions: boolean = false): { version: string | null, data: any[] } => {
  console.log(`🔍 [getTags] Fetching tags for company: ${slug}, version: ${version}, allVersions: ${allVersions}`);
  
  // Get state from Redux store
  const state = store.getState();

  // Get company data
  const company = state.company.company;

  // Get maps data
  const maps = state.maps.maps;

  // If no company or maps data, return empty result
  if (!company || !maps || maps.length === 0) {
    return { version: null, data: [] };
  }

  // Get pages from company data
  const pages = company.pages || [];

  // If no pages, return empty result
  if (pages.length === 0) {
    return { version: null, data: [] };
  }

  // Get all tags from all pages
  let allTags: any[] = [];

  pages.forEach(page => {
    // Check if page has tags
    if (page.tags && Array.isArray(page.tags)) {
      // Add page_id to each tag for reference
      const tagsWithPageId = page.tags.map(tag => ({
        ...tag,
        page_id: page.id
      }));

      allTags = [...allTags, ...tagsWithPageId];
    }
  });

  // If no tags found, return empty result
  if (allTags.length === 0) {
    return { version: null, data: [] };
  }

  // If allVersions is true, return all tags without filtering by version
  if (allVersions) {
    console.log(`✅ [getTags] Returning all ${allTags.length} tags without version filtering`);
    return {
      version: null,
      data: allTags
    };
  }

  let versionToUse = version;
  let versionMaps: any[] = [];

  // If no version specified, try to find a version with non-empty tags
  if (!versionToUse) {
    // Extract all versions from maps
    const versions = maps
      .map(map => map.version)
      .filter(v => v !== undefined && v !== null);

    if (versions.length === 0) {
      return { version: null, data: [] };
    }

    // Sort versions in descending order (assuming version is a date string like YYYYMMDD)
    versions.sort((a, b) => b.localeCompare(a));

    // Try each version in order until we find one with tags
    let foundVersion = null;

    for (const candidateVersion of versions) {
      // Filter maps for this version
      const candidateMaps = maps.filter(map => map.version === candidateVersion);

      // Check if any map has tags
      let hasTags = false;

      for (const map of candidateMaps) {
        const mapData = map || {};

        if ((Array.isArray(mapData.tags) && mapData.tags.length > 0) ||
          (mapData.tags && typeof mapData.tags === 'object' && !Array.isArray(mapData.tags) && Object.keys(mapData.tags).length > 0)) {
          hasTags = true;
          break;
        }
      }

      if (hasTags) {
        foundVersion = candidateVersion;
        break;
      }
    }

    // If we found a version with tags, use it; otherwise use the most recent
    versionToUse = foundVersion || versions[0];
  }

  // Filter maps to only include those for the specified/most recent version
  versionMaps = maps.filter(map => map.version === versionToUse);

  // If no maps for this version, return empty result with the version
  if (versionMaps.length === 0) {
    return { version: versionToUse, data: [] };
  }

  // Create a set of tag IDs that exist in the maps for this version
  const tagIdsInMaps = new Set<string>();

  versionMaps.forEach(map => {
    const mapData = map || {};

    // Try different possible structures for tags
    // 1. Direct array of tags
    if (Array.isArray(mapData.tags)) {
      mapData.tags.forEach((tagItem: any) => {
        if (tagItem && tagItem.page_tag_id) {
          tagIdsInMaps.add(tagItem.page_tag_id);
        } else if (tagItem && tagItem.tag_id) {
          tagIdsInMaps.add(tagItem.tag_id);
        } else if (typeof tagItem === 'string') {
          tagIdsInMaps.add(tagItem);
        }
      });
    }

    // 2. Object with tag IDs as keys
    if (mapData.tags && typeof mapData.tags === 'object' && !Array.isArray(mapData.tags)) {
      const tagKeys = Object.keys(mapData.tags);
      tagKeys.forEach(tagId => {
        tagIdsInMaps.add(tagId);
      });
    }
  });

  // If no tag IDs found, return empty result with null version
  // This indicates we tried but found no valid tags
  if (tagIdsInMaps.size === 0) {
    return { version: null, data: [] };
  }

  // Filter tags to only include those that exist in the maps for this version
  const filteredTags = allTags.filter(tag => {
    // If tag has no ID, exclude it
    if (!tag.id) {
      return false;
    }

    // Check if tag ID exists in maps
    return tagIdsInMaps.has(tag.id);
  });

  console.log(`✅ [getTags] Filtered to ${filteredTags.length} tags for version ${versionToUse}`);

  // Return both the version and the filtered tags
  return {
    version: versionToUse,
    data: filteredTags
  };
};

/**
 * Get one version per quarter from a list of versions
 * @param versions Array of version strings in YYYYMMDD format
 * @returns Array of version strings with one version per quarter (the most recent one in each quarter)
 */
export const getQuarterlyVersions = (versions: string[]): string[] => {
  if (!versions || versions.length === 0) return [];

  // Sort versions in descending order (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.localeCompare(a));

  // Map to store the most recent version for each quarter
  const quarterMap = new Map<string, string>();

  // Process each version
  sortedVersions.forEach(version => {
    // Get the quarter for this version
    const quarter = versionToQuarter(version);

    // If we haven't seen this quarter yet, or if this version is more recent than the one we have
    if (quarter && !quarterMap.has(quarter)) {
      quarterMap.set(quarter, version);
    }
  });

  // Convert the map back to an array of versions, sorted by quarter (newest first)
  return Array.from(quarterMap.values()).sort((a, b) => b.localeCompare(a));
};

/**
 * Get all available versions for a specific company or page from the scrapes data
 * @param subslug The page subslug (optional - if not provided, gets versions across all pages)
 * @returns An array of version strings sorted in descending order (newest first)
 */
export const getVersions = (subslug?: string): string[] => {
  // Get state from Redux store
  const state = store.getState();

  // Get company data
  const company = state.company.company;

  // If no company data, return empty array
  if (!company) {
    console.log('⚠️ [getVersions] No company data found');
    return [];
  }

  // Get pages from company data
  const pages = company.pages || [];

  // If no pages, return empty array
  if (pages.length === 0) {
    console.log('⚠️ [getVersions] No pages found for company');
    return [];
  }

  // Filter pages by subslug if provided
  const relevantPages = subslug
    ? pages.filter(page => page.subslug === subslug)
    : pages;

  if (subslug && relevantPages.length === 0) {
    console.log(`⚠️ [getVersions] No page found with subslug: ${subslug}`);
    return [];
  }

  // Collect all scrapes from relevant pages
  let allScrapes: any[] = [];

  relevantPages.forEach(page => {
    // Check if page has scrapes
    if (page.scrapes && Array.isArray(page.scrapes)) {
      allScrapes = [...allScrapes, ...page.scrapes];
    }
  });

  // If no scrapes found, return empty array
  if (allScrapes.length === 0) {
    console.log(`⚠️ [getVersions] No scrapes found for ${subslug ? `page with subslug ${subslug}` : 'company pages'}`);
    return [];
  }

  // Extract unique versions from scrapes
  const uniqueVersions = [...new Set(allScrapes.map(scrape => scrape.version))];

  // Sort versions in descending order (newest first)
  uniqueVersions.sort((a, b) => b.localeCompare(a));

  console.log(`✅ [getVersions] Found ${uniqueVersions.length} unique versions ${subslug ? `for page with subslug ${subslug}` : 'across all pages'}`);

  return uniqueVersions;
};
